/**
 * ContinuumCX — top-level app.
 *
 * Order of providers (outer → inner):
 *   QueryClientProvider   data fetching
 *   ThemeProvider         light/dark + tailwind class on <html>
 *   AuthProvider          user + auth flow state machine
 *   BrowserRouter         routing
 *
 * <AuthRouter> gates the rest of the app: if the user isn't fully authenticated
 * it renders the right step in the auth flow (login / password reset / MFA
 * enroll / MFA verify) instead of the portal.
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from './theme/ThemeProvider';
import { AuthProvider } from './auth/AuthProvider';
import AuthRouter from './auth/AuthRouter';
import PortalShell from './portal/PortalShell';
import RoleGuard from './portal/RoleGuard';
import LandingRedirect from './portal/LandingRedirect';
import HelpIndexPage from './portal/help/HelpIndexPage';
import UserGuidePage from './portal/help/UserGuidePage';

import SoftphonePage from './portal/pages/SoftphonePage';
import AccountPage from './portal/pages/AccountPage';
import PlaceholderPage from './portal/pages/PlaceholderPage';
import AdminLayout from './portal/pages/admin/AdminLayout';
import AgentsPage from './portal/pages/admin/AgentsPage';
import QueuesPage from './portal/pages/admin/QueuesPage';
import AnnouncementsPage from './portal/pages/admin/AnnouncementsPage';
import RecordingPage from './portal/pages/admin/RecordingPage';
import ClustersPage from './portal/pages/admin/ClustersPage';
import DidsPage from './portal/pages/admin/DidsPage';
import DialingProfilesPage from './portal/pages/admin/DialingProfilesPage';
import AuxCodesPage from './portal/pages/admin/AuxCodesPage';
import QaFormsPage from './portal/pages/admin/QaFormsPage';
import ReportsLayout, { ReportsIndexRedirect } from './portal/pages/reports/ReportsLayout';
import AgentProductivity from './portal/pages/reports/AgentProductivity';
import AgentByInteraction from './portal/pages/reports/AgentByInteraction';
import AgentStateSummary from './portal/pages/reports/AgentStateSummary';
import AuxReasons from './portal/pages/reports/AuxReasons';
import AgentStateTimeline from './portal/pages/reports/AgentStateTimeline';
import QueuePerformance from './portal/pages/reports/QueuePerformance';
import CDRDrilldown from './portal/pages/reports/CDRDrilldown';
import TrunkPerformance from './portal/pages/reports/TrunkPerformance';
import AcdHistorical from './portal/pages/reports/AcdHistorical';
import RecordingCoverage from './portal/pages/reports/RecordingCoverage';
import CoverageGaps from './portal/pages/reports/CoverageGaps';
import QaScorecards from './portal/pages/reports/QaScorecards';
import TransferReport from './portal/pages/reports/TransferReport';
import OutboundCompliance from './portal/pages/reports/OutboundCompliance';
import StorageArchive from './portal/pages/reports/StorageArchive';
import MyAssignments from './portal/pages/qa/MyAssignments';
import QaAssignPage from './portal/pages/qa/AssignPage';
import QaManagerOverview from './portal/pages/qa/QaManagerOverview';
import QaInsights from './portal/pages/qa/QaInsights';
import FailoverPage from './portal/pages/admin/FailoverPage';
import AcdLayout, { AcdIndexRedirect } from './portal/pages/acd/AcdLayout';
import FloorWallboard from './portal/pages/acd/FloorWallboard';
import QueuesView from './portal/pages/acd/QueuesView';
import AgentStateView from './portal/pages/acd/AgentStateView';
import AgentPersonalView from './portal/pages/acd/AgentPersonalView';
import SupervisorTools from './portal/pages/acd/SupervisorTools';
import TrunksView from './portal/pages/acd/TrunksView';
import RecordingsBrowserPage from './portal/pages/RecordingsBrowserPage';
import PlaybackPopout from './portal/pages/PlaybackPopout';
import SettingsLayout from './portal/pages/settings/SettingsLayout';
import PermissionsPage from './portal/pages/settings/PermissionsPage';
import UsersPage from './portal/pages/settings/UsersPage';
import ClientsPage from './portal/pages/settings/ClientsPage';

import './styles/tailwind.css';
import './styles/theme.css';   // existing softphone CSS — keep last
import './App.css';            // existing softphone-level CSS — keep last

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AuthRouter>
              <Routes>
                {/* Popout playback — bare layout, no sidebar. */}
                <Route
                  path="/playback/:recordingId"
                  element={<RoleGuard modules={['recordings_browser']}><PlaybackPopout /></RoleGuard>}
                />
                <Route element={<PortalShell />}>
                  <Route index element={<LandingRedirect />} />
                  <Route path="/softphone" element={<SoftphonePage />} />
                  <Route
                    path="/acd"
                    element={<RoleGuard modules={['acd_floor','acd_queues','acd_agent_state','acd_personal','acd_supervisor','acd_trunks']}><AcdLayout /></RoleGuard>}
                  >
                    <Route index element={<AcdIndexRedirect />} />
                    <Route path="floor"    element={<RoleGuard modules={['acd_floor']}><FloorWallboard /></RoleGuard>} />
                    <Route path="queues"   element={<RoleGuard modules={['acd_queues']}><QueuesView /></RoleGuard>} />
                    <Route path="agent-state" element={<RoleGuard modules={['acd_agent_state']}><AgentStateView /></RoleGuard>} />
                    <Route path="personal" element={<RoleGuard modules={['acd_personal']}><AgentPersonalView /></RoleGuard>} />
                    <Route path="supervisor" element={<RoleGuard modules={['acd_supervisor']}><SupervisorTools /></RoleGuard>} />
                    <Route path="trunks" element={<RoleGuard modules={['acd_trunks']}><TrunksView /></RoleGuard>} />
                  </Route>
                  <Route
                    path="/admin"
                    element={<RoleGuard allow={['admin']}><AdminLayout /></RoleGuard>}
                  >
                    <Route index element={<Navigate to="agents" replace />} />
                    <Route path="agents" element={<AgentsPage />} />
                    <Route path="queues" element={<QueuesPage />} />
                    <Route path="announcements" element={<AnnouncementsPage />} />
                    <Route path="recording" element={<RecordingPage />} />
                    <Route path="clusters" element={<ClustersPage />} />
                    <Route path="dids" element={<DidsPage />} />
                    <Route path="dialing-profiles" element={<DialingProfilesPage />} />
                    <Route path="aux-codes" element={<AuxCodesPage />} />
                    <Route path="qa-forms" element={<QaFormsPage />} />
                    <Route path="qa-assign" element={<QaAssignPage />} />
                    <Route path="failover" element={<FailoverPage />} />
                  </Route>
                  <Route
                    path="/qa/my-assignments"
                    element={<RoleGuard allow={['admin','qa','qa_manager','supervisor']}><MyAssignments /></RoleGuard>}
                  />
                  <Route
                    path="/qa-manager"
                    element={<RoleGuard allow={['admin','qa_manager']}><QaManagerOverview /></RoleGuard>}
                  />
                  <Route
                    path="/qa/insights"
                    element={<RoleGuard modules={['qa_insights']}><QaInsights /></RoleGuard>}
                  />
                  <Route
                    path="/recordings"
                    element={<RoleGuard modules={['recordings_browser']}><RecordingsBrowserPage /></RoleGuard>}
                  />
                  <Route
                    path="/settings"
                    element={<RoleGuard allow={['admin']}><SettingsLayout /></RoleGuard>}
                  >
                    <Route index element={<Navigate to="permissions" replace />} />
                    <Route path="permissions" element={<PermissionsPage />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="clients" element={<ClientsPage />} />
                  </Route>
                  <Route
                    path="/reports"
                    element={<RoleGuard modules={['reports']}><ReportsLayout /></RoleGuard>}
                  >
                    <Route index element={<ReportsIndexRedirect />} />
                    <Route path="agents" element={<RoleGuard modules={['reports']}><AgentProductivity /></RoleGuard>} />
                    <Route path="agents-by-interaction" element={<RoleGuard modules={['reports']}><AgentByInteraction /></RoleGuard>} />
                    <Route path="agent-state" element={<RoleGuard modules={['reports']}><AgentStateSummary /></RoleGuard>} />
                    <Route path="agent-timeline" element={<RoleGuard modules={['reports']}><AgentStateTimeline /></RoleGuard>} />
                    <Route path="aux-reasons" element={<RoleGuard modules={['reports']}><AuxReasons /></RoleGuard>} />
                    <Route path="queues" element={<RoleGuard modules={['reports']}><QueuePerformance /></RoleGuard>} />
                    <Route path="cdr" element={<RoleGuard modules={['reports']}><CDRDrilldown /></RoleGuard>} />
                    <Route path="trunks" element={<RoleGuard modules={['reports']}><TrunkPerformance /></RoleGuard>} />
                    <Route path="acd" element={<RoleGuard modules={['reports']}><AcdHistorical /></RoleGuard>} />
                    <Route path="coverage" element={<RoleGuard modules={['reports']}><RecordingCoverage /></RoleGuard>} />
                    <Route path="coverage-gaps" element={<RoleGuard modules={['reports']}><CoverageGaps /></RoleGuard>} />
                    <Route path="qa" element={<RoleGuard modules={['reports']}><QaScorecards /></RoleGuard>} />
                    <Route path="transfers" element={<RoleGuard modules={['reports']}><TransferReport /></RoleGuard>} />
                    <Route path="compliance" element={<RoleGuard modules={['reports']}><OutboundCompliance /></RoleGuard>} />
                    <Route path="storage" element={<RoleGuard modules={['reports']}><StorageArchive /></RoleGuard>} />
                  </Route>
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/help" element={<HelpIndexPage />} />
                  <Route path="/help/guides/:slug" element={<UserGuidePage />} />
                  <Route path="*" element={<LandingRedirect />} />
                </Route>
              </Routes>
            </AuthRouter>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
